import { useRef, useCallback, useEffect, useState } from 'react';
import * as Tone from 'tone';

/**
 * Audio hook using Tone.js with Salamander Grand Piano samples
 * Provides realistic piano sounds for correct answers and a soft chime for incorrect
 */
export function useAudio(enabled = true) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pianoRef = useRef(null);
  const chimeSynthRef = useRef(null);
  const initializedRef = useRef(false);

  // Initialize Tone.js with Salamander piano samples
  const initAudio = useCallback(async () => {
    if (initializedRef.current || !enabled) return;
    
    try {
      setIsLoading(true);
      
      // Start audio context on user gesture (required for iOS/mobile)
      await Tone.start();
      
      // For iOS: ensure the audio context is running
      if (Tone.context.state !== 'running') {
        await Tone.context.resume();
      }
      
      // Create piano sampler with Salamander Grand Piano samples
      // Only sample key notes - Tone.Sampler will interpolate the rest
      pianoRef.current = new Tone.Sampler({
        urls: {
          'A0': 'A0.mp3',
          'C1': 'C1.mp3',
          'D#1': 'Ds1.mp3',
          'F#1': 'Fs1.mp3',
          'A1': 'A1.mp3',
          'C2': 'C2.mp3',
          'D#2': 'Ds2.mp3',
          'F#2': 'Fs2.mp3',
          'A2': 'A2.mp3',
          'C3': 'C3.mp3',
          'D#3': 'Ds3.mp3',
          'F#3': 'Fs3.mp3',
          'A3': 'A3.mp3',
          'C4': 'C4.mp3',
          'D#4': 'Ds4.mp3',
          'F#4': 'Fs4.mp3',
          'A4': 'A4.mp3',
          'C5': 'C5.mp3',
          'D#5': 'Ds5.mp3',
          'F#5': 'Fs5.mp3',
          'A5': 'A5.mp3',
          'C6': 'C6.mp3',
          'D#6': 'Ds6.mp3',
          'F#6': 'Fs6.mp3',
          'A6': 'A6.mp3',
          'C7': 'C7.mp3',
          'D#7': 'Ds7.mp3',
          'F#7': 'Fs7.mp3',
          'A7': 'A7.mp3',
          'C8': 'C8.mp3',
        },
        release: 1.2,
        baseUrl: 'https://tonejs.github.io/audio/salamander/',
        onload: () => {
          console.log('Salamander piano samples loaded');
        }
      }).toDestination();
      
      // Set appropriate volume
      pianoRef.current.volume.value = -6;
      
      // Create soft chime synth for incorrect feedback (descending)
      chimeSynthRef.current = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: 'sine' },
        envelope: { 
          attack: 0.01, 
          decay: 0.3, 
          sustain: 0.1, 
          release: 0.8 
        }
      }).toDestination();
      chimeSynthRef.current.volume.value = -14;
      
      // Wait for all samples to load
      await Tone.loaded();
      
      initializedRef.current = true;
      setIsLoaded(true);
      console.log('Audio system ready with Salamander piano');
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  // Play a piano note using Salamander samples
  const playNote = useCallback(async (noteName, duration = '4n') => {
    if (!enabled || !pianoRef.current || !initializedRef.current) return;
    
    try {
      // Ensure audio context is running (iOS fix)
      if (Tone.context.state !== 'running') {
        await Tone.context.resume();
      }
      pianoRef.current.triggerAttackRelease(noteName, duration);
    } catch (error) {
      console.error('Error playing note:', error);
    }
  }, [enabled]);

  // Play correct feedback - plays the actual correct note with Salamander piano
  const playCorrectNote = useCallback(async (noteName) => {
    if (!enabled || !pianoRef.current || !initializedRef.current) return;
    
    try {
      // Ensure audio context is running (iOS fix)
      if (Tone.context.state !== 'running') {
        await Tone.context.resume();
      }
      // Play the actual piano note with a satisfying sustain
      pianoRef.current.triggerAttackRelease(noteName, '2n');
    } catch (error) {
      console.error('Error playing correct note:', error);
    }
  }, [enabled]);

  // Play incorrect feedback sound - soft descending chime
  const playIncorrect = useCallback(async () => {
    if (!enabled || !chimeSynthRef.current) return;
    
    try {
      // Ensure audio context is running (iOS fix)
      if (Tone.context.state !== 'running') {
        await Tone.context.resume();
      }
      // Soft descending chime (gentle "wrong" sound)
      const now = Tone.now();
      chimeSynthRef.current.triggerAttackRelease('E4', '16n', now);
      chimeSynthRef.current.triggerAttackRelease('C4', '16n', now + 0.1);
      chimeSynthRef.current.triggerAttackRelease('A3', '8n', now + 0.2);
    } catch (error) {
      console.error('Error playing incorrect sound:', error);
    }
  }, [enabled]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pianoRef.current) {
        pianoRef.current.dispose();
      }
      if (chimeSynthRef.current) {
        chimeSynthRef.current.dispose();
      }
    };
  }, []);

  return {
    isLoaded,
    isLoading,
    initAudio,
    playNote,
    playCorrectNote,
    playIncorrect
  };
}

export default useAudio;
